import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Stack, TextField, MenuItem, Button, Typography, InputAdornment, Alert,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getTimeFormat } from '@utils/dateHelper';
import { resolveDayWindow, slotOptions, minutesToHHMM } from './timeGrid';
import { brand } from '../../../theme/tokens';
import type { Reservation } from '@app-types/models/reservation';
import type { Plan } from '@app-types/models/plan';
import type { Stylist } from '@app-types/models/stylist';
import type { Business } from '@app-types/models/business';

const DURATIONS = [15, 30, 45, 60, 90, 120];

export interface FormValues {
  date: Dayjs;
  startTime: string;
  duration: number;
  planId: string;
  originalAmount: number | string;
  discountedAmount: number | string;
  customerName: string;
  customerPhone: string;
  remark: string;
  blockTitle: string;
}

export interface BookingPayload {
  customerName: string;
  customerPhone: string;
  planId: string;
  originalAmount: number;
  discountedAmount: number;
  remark: string;
  start: Date;
  end: Date;
  staffId?: string;
}

export interface BlockPayload {
  start: Date;
  end: Date;
  title: string;
}

// Combine a dayjs date + "HH:MM" into a JS Date.
const combine = (date: Dayjs, hhmm: string): Date => {
  const [h, m] = hhmm.split(':').map(Number);
  return dayjs(date).hour(h).minute(m).second(0).millisecond(0).toDate();
};

const durationOf = (r: Reservation): number => Math.round((new Date(r.end).getTime() - new Date(r.start).getTime()) / 60000);
const overlaps = (s: number, e: number, rs: number, re: number): boolean => s < re && rs < e;

const BLOCKING_STATUSES = new Set(['confirmed', 'pending-for-approval']);

interface NewBookingDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: BookingPayload) => void | Promise<void>;
  onUpdate?: (id: string, payload: BookingPayload) => void | Promise<void>;
  onBlock?: (payload: BlockPayload) => void | Promise<void>;
  plans: Plan[];
  stylist?: Stylist | null;
  business?: Business | null;
  existingReservations?: Reservation[];
  prefill?: { date?: Date | string | Dayjs | null; startTime?: string | null } | null;
  editReservation?: Reservation | null;
}

// Create a booking (walk-in), edit/reschedule an existing booking, OR block a period — for the v2 dashboard.
const NewBookingDialog: React.FC<NewBookingDialogProps> = ({ open, onClose, onCreate, onUpdate, onBlock, plans, stylist, business, existingReservations = [], prefill, editReservation }) => {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState('book'); // 'book' | 'block'
  const isEdit = !!editReservation;

  const planList = Array.isArray(plans) ? plans : ((plans as unknown as { plans?: Plan[] }).plans || []);
  const { startMin, endMin } = resolveDayWindow(stylist, business);

  // Ensure the edited booking's start-time and duration are always selectable, even if off the 15-min grid.
  const startTimes = useMemo(() => {
    const base = slotOptions(startMin, endMin, 15);
    if (isEdit) {
      const tm = dayjs(editReservation!.start).format('HH:mm');
      if (!base.includes(tm)) return [...base, tm].sort();
    }
    return base;
  }, [startMin, endMin, isEdit, editReservation]);

  const durationOptions = useMemo(() => {
    const set = new Set(DURATIONS);
    if (isEdit) set.add(durationOf(editReservation!));
    return [...set].sort((a, b) => a - b);
  }, [isEdit, editReservation]);

  const defaults = (): FormValues => {
    if (isEdit) {
      const er = editReservation!;
      const s = dayjs(er.start);
      return {
        date: s,
        startTime: s.format('HH:mm'),
        duration: durationOf(er),
        planId: er.planId?._id || '',
        originalAmount: er.originalAmount ?? '',
        discountedAmount: er.discountedAmount ?? '',
        customerName: er.customerName || '',
        customerPhone: er.customerPhone || er.userId?.phone || '',
        remark: er.remark || '',
        blockTitle: '',
      };
    }
    return {
      date: prefill?.date ? dayjs(prefill.date) : dayjs(),
      startTime: prefill?.startTime || startTimes[0] || '10:00',
      duration: 60,
      planId: '',
      originalAmount: '',
      discountedAmount: '',
      customerName: '',
      customerPhone: '',
      remark: '',
      blockTitle: '',
    };
  };

  const { control, handleSubmit, register, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm<FormValues>({
    defaultValues: defaults(),
    shouldUnregister: true,
  });

  // Re-seed the form each time it opens (fresh prefill / edit target); always start in booking mode.
  useEffect(() => {
    if (open) { setMode('book'); reset(defaults()); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editReservation]);

  const date = watch('date');
  const startTime = watch('startTime');
  const duration = watch('duration');
  const endLabel = useMemo(() => {
    if (!startTime) return '';
    const [h, m] = startTime.split(':').map(Number);
    return minutesToHHMM(h * 60 + m + Number(duration));
  }, [startTime, duration]);

  const range = useMemo(() => {
    if (!date || !startTime) return null;
    const s = combine(date, startTime).getTime();
    return { s, e: s + Number(duration) * 60 * 1000 };
  }, [date, startTime, duration]);

  // Booking-mode warnings: overlap with another booking, or with a blocked period. (Ignore the booking being edited.)
  const bookingConflict = useMemo(() => {
    if (mode !== 'book' || !range) return null;
    return existingReservations.find((r) =>
      r._id !== editReservation?._id && BLOCKING_STATUSES.has(r.status)
      && overlaps(range.s, range.e, new Date(r.start).getTime(), new Date(r.end).getTime()),
    ) || null;
  }, [mode, range, existingReservations, editReservation]);

  const blockConflict = useMemo(() => {
    if (mode !== 'book' || !range) return null;
    const slots = stylist?.availability?.timeSlots || [];
    return slots.find((sl) =>
      sl.isOpen === false && overlaps(range.s, range.e, new Date(sl.start).getTime(), new Date(sl.end as string | Date).getTime()),
    ) || null;
  }, [mode, range, stylist]);

  const handlePlanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const plan = planList.find((p) => p._id === e.target.value);
    setValue('planId', e.target.value, { shouldValidate: true });
    setValue('originalAmount', (plan ? plan.price : '') as number | string);
    setValue('discountedAmount', (plan ? plan.price : '') as number | string);
  };

  const submit = async (data: FormValues) => {
    const start = combine(data.date, data.startTime);
    const end = new Date(start.getTime() + Number(data.duration) * 60 * 1000);
    if (mode === 'block' && !isEdit) {
      await onBlock!({ start, end, title: data.blockTitle });
      return;
    }
    const payload = {
      customerName: data.customerName,
      customerPhone: data.customerPhone,
      planId: data.planId,
      originalAmount: Number(data.originalAmount) || 0,
      discountedAmount: Number(data.discountedAmount) || 0,
      remark: data.remark,
      start,
      end,
    };
    if (isEdit) {
      await onUpdate!(editReservation!._id, { ...payload, staffId: editReservation!.staffId?._id || stylist?._id });
    } else {
      if (!stylist?._id) return; // no stylist selected — nothing to book against
      await onCreate({ ...payload, staffId: stylist._id });
    }
  };

  const title = isEdit ? t('reservationsV2.editBooking') : (mode === 'block' ? t('reservationsV2.blockTime') : t('reservationsV2.newBooking'));
  const submitLabel = isEdit ? t('common.save') : (mode === 'block' ? t('reservationsV2.newBookingDialog.createBlock') : t('reservationsV2.newBookingDialog.create'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <form onSubmit={handleSubmit(submit)}>
        <DialogContent dividers>
          <Stack spacing={2}>
            {!isEdit && (
              <ToggleButtonGroup
                exclusive fullWidth size="small" value={mode}
                onChange={(_, v) => v && setMode(v)}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600 } }}
              >
                <ToggleButton value="book">{t('reservationsV2.newBookingDialog.modeBook')}</ToggleButton>
                <ToggleButton value="block">{t('reservationsV2.newBookingDialog.modeBlock')}</ToggleButton>
              </ToggleButtonGroup>
            )}

            <Typography variant="body2" color="text.secondary">
              {t('reservationList.stylist')}: <b>{stylist?.displayName}</b>
            </Typography>

            {mode === 'book' && bookingConflict && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                {t('reservationsV2.newBookingDialog.conflictWarning', {
                  name: bookingConflict.customerName,
                  time: `${getTimeFormat(bookingConflict.start)}–${getTimeFormat(bookingConflict.end)}`,
                })}
              </Alert>
            )}
            {mode === 'book' && blockConflict && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                {t('reservationsV2.newBookingDialog.blockOverlapWarning', {
                  title: blockConflict.title || t('reservationsV2.blocked'),
                })}
              </Alert>
            )}

            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label={t('reservationsV2.newBookingDialog.date')}
                  value={field.value}
                  onChange={field.onChange}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  minDate={isEdit ? dayjs().subtract(1, 'month').startOf('day') : dayjs().subtract(1, 'day')}
                  maxDate={dayjs().add(6, 'month')}
                />
              )}
            />

            <Stack direction="row" spacing={1.5}>
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <TextField select fullWidth size="small" label={t('reservationsV2.newBookingDialog.startTime')} {...field}>
                    {startTimes.map((tm) => (<MenuItem key={tm} value={tm}>{tm}</MenuItem>))}
                  </TextField>
                )}
              />
              <Controller
                name="duration"
                control={control}
                render={({ field }) => (
                  <TextField
                    select fullWidth size="small" label={t('reservationsV2.newBookingDialog.duration')} {...field}
                    helperText={t('reservationsV2.newBookingDialog.endsAt', { time: endLabel })}
                  >
                    {durationOptions.map((d) => (<MenuItem key={d} value={d}>{t('reservationsV2.newBookingDialog.minutes', { n: d })}</MenuItem>))}
                  </TextField>
                )}
              />
            </Stack>

            {mode === 'block' && !isEdit ? (
              <TextField
                fullWidth size="small" label={t('reservationsV2.newBookingDialog.blockLabel')}
                placeholder={t('reservationsV2.newBookingDialog.blockLabelPlaceholder')}
                {...register('blockTitle')}
              />
            ) : (
              <>
                <Controller
                  name="planId"
                  control={control}
                  rules={{ required: t('validation.planRequired') }}
                  render={({ field }) => (
                    <TextField
                      select fullWidth size="small" label={t('reservationsV2.newBookingDialog.service')}
                      value={field.value}
                      onChange={handlePlanChange}
                      error={!!errors.planId}
                      helperText={errors.planId?.message}
                    >
                      {planList.map((p) => (
                        <MenuItem key={p._id} value={p._id}>
                          {i18n.language === 'zh' ? (p.zh_name || p.name) : p.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />

                <Stack direction="row" spacing={1.5}>
                  <TextField
                    fullWidth size="small" type="number" label={t('reservationsV2.newBookingDialog.originalAmount')}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    {...register('originalAmount')}
                  />
                  <TextField
                    fullWidth size="small" type="number" label={t('reservationsV2.newBookingDialog.finalAmount')}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    {...register('discountedAmount')}
                  />
                </Stack>

                <TextField
                  fullWidth size="small" label={t('reservationsV2.newBookingDialog.customer')}
                  error={!!errors.customerName}
                  helperText={errors.customerName?.message}
                  {...register('customerName', { required: t('validation.customerNameRequired') })}
                />
                <TextField fullWidth size="small" label={t('reservationsV2.newBookingDialog.phone')} {...register('customerPhone')} />
                <TextField fullWidth size="small" multiline minRows={2} label={t('reservationsV2.newBookingDialog.note')} {...register('remark')} />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} variant="outlined" sx={{ color: brand.ink, borderColor: brand.border, '&:hover': { borderColor: brand.ink, bgcolor: brand.cream } }}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" color={(mode === 'block' && !isEdit) ? 'inherit' : 'primary'} disabled={isSubmitting}>
            {submitLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default NewBookingDialog;
